import { createSelector } from '@reduxjs/toolkit';

import type { AccentItem, LanguageItem, StyleItem, ToneItem } from './ai-voices-slices';

import type {
    AccentEnum,
    AIVoice,
    AIVoiceStyleEnum,
    AIVoiceToneEnum,
    LanguageEnum,
} from 'js/src/client';
import StoreORM from 'js/src/data/store-orm';
import { type ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export function getAIVoiceIds(state: RootState): Immutable.List<string> {
    const orm = new StoreORM(state.aiVoicesAndAvatars.aiVoices);
    return orm.getAllIds();
}

export function getAIVoiceById(state: RootState, id: string): ImmutableMap<AIVoice> | null {
    const orm = new StoreORM(state.aiVoicesAndAvatars.aiVoices);
    const voice = orm.getByIdOrUndefined(id);
    return voice || null;
}

export const getAIVoiceLanguages = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices2.languages,
    (languagesData): LanguageItem[] => {
        return languagesData.allIds
            .map((id) => languagesData.byId[id])
            .sort((a, b) => a.display.localeCompare(b.display));
    },
);

export const getAIVoiceAccents = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices2.accents,
    (accentsData): AccentItem[] => {
        return accentsData.allIds
            .map((id) => accentsData.byId[id])
            .sort((a, b) => a.display.localeCompare(b.display));
    },
);

export const getAIVoiceTones = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices2.tones,
    (tonesData): ToneItem[] => {
        return tonesData.allIds
            .map((id) => tonesData.byId[id])
            .sort((a, b) => a.display.localeCompare(b.display));
    },
);

export const getAIVoiceStyles = createSelector(
    (state: RootState) => state.aiVoicesAndAvatars.aiVoices2.styles,
    (stylesData): StyleItem[] => {
        return stylesData.allIds
            .map((id) => stylesData.byId[id])
            .sort((a, b) => a.display.localeCompare(b.display));
    },
);

export function getSelectedLanguages(state: RootState): LanguageEnum[] {
    return state.aiVoicesAndAvatars.aiVoices2.filters.languages;
}

export function getSelectedStyle(state: RootState): AIVoiceStyleEnum | null {
    return state.aiVoicesAndAvatars.aiVoices2.filters.style;
}

export function getSelectedAccent(state: RootState): AccentEnum | null {
    return state.aiVoicesAndAvatars.aiVoices2.filters.accent;
}

export function getSelectedTone(state: RootState): AIVoiceToneEnum | null {
    return state.aiVoicesAndAvatars.aiVoices2.filters.tone;
}

export function getSelectedSearchTerm(state: RootState): string {
    return state.aiVoicesAndAvatars.aiVoices2.filters.searchTerm;
}
